const STORAGE_KEY = 'music24TeamInviteToken';
const TOKEN_PARAM = 'token';
// `team.accept`'s input schema (`music24-api/src/trpc/team.router.ts`) is `z.string().min(20).max(128)`.
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{20,128}$/;

// A private-browser fallback for `readInviteToken`/`clearInviteToken` when `sessionStorage`
// throws (private mode in some browsers). Covers only this page load; the sign-in round trip
// still needs `sessionStorage` to survive the full-page reload `PublicOnlyRouteController` does.
let memoryToken: string | null = null;

/**
 * The invite token from a URL fragment, or `null`. The token never travels in a query string
 * (it would land in server access logs and the `Referer` header of any page it links to), so a
 * `?token=` value is rejected the same as a missing one.
 */
export function extractInviteToken(hash: string): string | null {
  if (!hash.startsWith('#')) {
    return null;
  }
  const token = new URLSearchParams(hash.slice(1)).get(TOKEN_PARAM);
  return token !== null && TOKEN_PATTERN.test(token) ? token : null;
}

/**
 * Reads the token out of the current URL's fragment, stashes it for the sign-in round trip, and
 * clears the fragment so it does not stay in browser history. Call this once, on the first
 * render of `/team/accept` — `PublicOnlyRouteController` does a full page reload back to this
 * route after sign-in, so a token found on an earlier render must already be in `sessionStorage`
 * by then, not still sitting in `window.location.hash`.
 */
export function captureInviteTokenFromLocation(): string | null {
  const token = extractInviteToken(window.location.hash);
  if (token !== null) {
    memoryToken = token;
    try {
      sessionStorage.setItem(STORAGE_KEY, token);
    } catch {
      /* Private browsing: the in-memory copy still covers this page load. */
    }
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
  }
  return readInviteToken();
}

/** The token captured earlier in this browsing session, or `null`. */
export function readInviteToken(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY) ?? memoryToken;
  } catch {
    return memoryToken;
  }
}

/** Drops the stashed token once the invite is accepted (or the user leaves the page for good). */
export function clearInviteToken(): void {
  memoryToken = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* Nothing to clear. */
  }
}
